import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Tabs } from "@shared/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import {
  XCircle,
  ArrowLeft,
  CreditCard,
  Calendar,
  FileText,
  MessageSquare,
  History,
  Printer,
  Clock,
} from "lucide-react";
import { PaymentAgreementDialog } from "./dialogueComponents/PaymentAgreementDialogue";
import { DisconnectionNoticeDialog } from "./dialogueComponents/DisconnectionNoticeDialogue";
import { SendStatementDialog } from "./dialogueComponents/SendStatementDialog";
import { ActionsTab } from "./tabComponents/actionsTab";
import { InstallmentsTab } from "./tabComponents/installmentsTab";
import { useState } from "react";
import {
  useAccountDetails,
  useDeletePaymentAgreement,
  usePaymentAgreementDetail,
} from "../../hooks";
import { useParams, useNavigate } from "react-router-dom";
import { useCurrency, useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { TimelineTab } from "@features/cx/shared/components/tabs/TimelineTab";
import { NotesTab } from "@features/cx/shared/components/tabs/NotesTab";
import { SendReminderDialog } from "./dialogueComponents/SendReminderDialogue";
import { CustomTooltip } from "@shared/components/CustomTooltip";

interface HistoryItem {
  id: string;
  date: string;
  type: "agreement" | "notice" | "communication" | "note";
  description: string;
  staff: string;
  details?: any;
}

interface Note {
  id: string;
  date: string;
  content: string;
  staff: string;
}

interface InstallmentSchedule {
  id: string;
  installmentNumber: number;
  dueDate: string;
  amount: number;
  status: "Pending" | "Paid" | "Overdue";
  paidDate?: string;
  paidAmount?: number;
}

interface AgreementDetails {
  id: string;
  createdDate: string;
  totalAmount: number;
  downPayment: number;
  monthlyPayment: number;
  installments: number;
  startDate: string;
  endDate: string;
  status: "Active" | "Completed" | "Cancelled";
  createdBy: string;
  notes?: string;
}

export function OutstandingAccountDetailPage() {
  const params = useParams();
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const queryParams = {
    remoteUtilityId: remoteUtilityId || 0,
    consumerNumber: params.consumerNumber || "",
  };
  const { data: account, isLoading, error } = useAccountDetails(queryParams);
  const { data: agreementDetail } = usePaymentAgreementDetail({
    remoteUtilityId: Number(remoteUtilityId),
    remoteBillId: Number(account?.result?.billData?.id),
  });
  const currency = useCurrency();
  const deletePaymentAgreement = useDeletePaymentAgreement(
    Number(remoteUtilityId),
    Number(account?.result?.billData?.id)
  );

  const [showAgreementDialog, setShowAgreementDialog] = useState(false);
  const [showNoticeDialog, setShowNoticeDialog] = useState(false);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [showStatementDialog, setShowStatementDialog] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [viewingHistoryItem, setViewingHistoryItem] =
    useState<HistoryItem | null>(null);

  const [history, setHistory] = useState<HistoryItem[]>([
    {
      id: "1",
      date: "2023-12-01T10:30:00Z",
      type: "agreement",
      description: "Payment agreement created - 6 installments of $150/month",
      staff: "Account Manager",
      details: {
        totalAmount: 900,
        downPayment: 0,
        monthlyPayment: 150,
        installments: 6,
        startDate: "2023-12-01",
      },
    },
    {
      id: "2",
      date: "2023-11-15T14:20:00Z",
      type: "note",
      description: "Customer called requesting payment extension",
      staff: "Customer Service Rep",
    },
  ]);

  const [notes, setNotes] = useState<Note[]>([
    {
      id: "1",
      date: "2023-12-01T10:30:00Z",
      content:
        "Customer has been cooperative with previous payment arrangements",
      staff: "Account Manager",
    },
    {
      id: "2",
      date: "2023-11-20T15:45:00Z",
      content: "Attempted phone contact - no answer, left voicemail",
      staff: "Collections Agent",
    },
  ]);

  const [installmentSchedule, setInstallmentSchedule] = useState<
    InstallmentSchedule[]
  >([
    {
      id: "1",
      installmentNumber: 1,
      dueDate: "2023-12-01",
      amount: 150,
      status: "Paid",
      paidDate: "2023-12-01",
      paidAmount: 150,
    },
    {
      id: "2",
      installmentNumber: 2,
      dueDate: "2024-01-01",
      amount: 150,
      status: "Paid",
      paidDate: "2024-01-02",
      paidAmount: 150,
    },
    {
      id: "3",
      installmentNumber: 3,
      dueDate: "2024-02-01",
      amount: 150,
      status: "Overdue",
    },
    {
      id: "4",
      installmentNumber: 4,
      dueDate: "2024-03-01",
      amount: 150,
      status: "Pending",
    },
    {
      id: "5",
      installmentNumber: 5,
      dueDate: "2024-04-01",
      amount: 150,
      status: "Pending",
    },
    {
      id: "6",
      installmentNumber: 6,
      dueDate: "2024-05-01",
      amount: 150,
      status: "Pending",
    },
  ]);

  const [agreementDetails, setAgreementDetails] = useState<AgreementDetails>({
    id: "AGR-001",
    createdDate: "2023-12-01T10:30:00Z",
    totalAmount: 900,
    downPayment: 0,
    monthlyPayment: 150,
    installments: 6,
    startDate: "2023-12-01",
    endDate: "2024-05-01",
    status: "Active",
    createdBy: "Account Manager",
    notes:
      "Customer requested 6-month payment plan due to temporary financial hardship.",
  });

  const formatCurrency = (amount: number) => {
    return currency + " " + amount;
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString || dateString.trim() === "") {
      return "N/A";
    }

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.warn(`Invalid date string: ${dateString}`);
        return "Invalid Date";
      }

      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date);
    } catch (error) {
      console.error(`Error formatting date: ${dateString}`, error);
      return "Invalid Date";
    }
  };

  const monthlyBills = [
    {
      month: "December 2023",
      waterCharges: 45.5,
      sewerCharges: 22.75,
      lateFees: 5.0,
      total: 73.25,
      dueDate: "2024-01-15",
      status: "Overdue",
    },
    {
      month: "November 2023",
      waterCharges: 42.3,
      sewerCharges: 21.15,
      lateFees: 5.0,
      total: 68.45,
      dueDate: "2023-12-15",
      status: "Overdue",
    },
    {
      month: "October 2023",
      waterCharges: 48.75,
      sewerCharges: 24.38,
      lateFees: 5.0,
      total: 78.13,
      dueDate: "2023-11-15",
      status: "Overdue",
    },
    {
      month: "September 2023",
      waterCharges: 51.2,
      sewerCharges: 25.6,
      lateFees: 5.0,
      total: 81.8,
      dueDate: "2023-10-15",
      status: "Overdue",
    },
  ];

  const totalOutstanding = monthlyBills.reduce(
    (sum, bill) => sum + bill.total,
    0
  );
  const hasActiveAgreement = history.some((item) => item.type === "agreement");

  // Tab handlers
  const handleProcessPayment = () => {
    // Add payment processing logic
    console.log("Process Payment clicked");
  };

  const handleCreateAgreement = () => {
    setShowAgreementDialog(true);
  };

  const handlePrintStatement = () => {
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Account Statement - ${account?.result?.consumerNo}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
              .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
              .section { margin-bottom: 25px; }
              .table { width: 100%; border-collapse: collapse; margin: 15px 0; }
              .table th, .table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              .table th { background-color: #f5f5f5; }
              .total-row { font-weight: bold; background-color: #f9f9f9; }
              @media print { body { margin: 20px; } }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>ACCOUNT STATEMENT</h1>
              <p>Water & Sewer Services</p>
              <p>Statement Date: ${formatDate(new Date().toISOString())}</p>
            </div>
            
            <div class="section">
              <h2>Account Information</h2>
              <p><strong>Account Number:</strong> ${
                account?.result?.consumerNo
              }</p>
              <p><strong>Customer Name:</strong> ${
                account?.result?.firstName + " " + account?.result?.lastName
              }</p>
              <p><strong>Service Address:</strong> ${
                account?.result?.serviceAddress
              }</p>
              <p><strong>Customer Type:</strong> ${
                account?.result?.customerType
              }</p>
            </div>

            <div class="section">
              <h2>Outstanding Balance Breakdown</h2>
              <table class="table">
                <thead>
                  <tr>
                    <th>Billing Period</th>
                    <th>Due Date</th>
                    <th>Water Charges</th>
                    <th>Sewer Charges</th>
                    <th>Late Fees</th>
                    <th>Total</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  ${monthlyBills
                    .map(
                      (bill) => `
                    <tr>
                      <td>${bill.month}</td>
                      <td>${formatDate(bill.dueDate)}</td>
                      <td>${formatCurrency(bill.waterCharges)}</td>
                      <td>${formatCurrency(bill.sewerCharges)}</td>
                      <td>${formatCurrency(bill.lateFees)}</td>
                      <td>${formatCurrency(bill.total)}</td>
                      <td>${bill.status}</td>
                    </tr>
                  `
                    )
                    .join("")}
                  <tr class="total-row">
                    <td colspan="5">Total Outstanding Balance</td>
                    <td>${formatCurrency(totalOutstanding)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="section">
              <h2>Account Summary</h2>
              <p><strong>Days Outstanding:</strong> ${
                account?.result?.daysOutstanding
              } days</p>
              <p><strong>Last Payment Date:</strong> ${formatDate(
                account?.result?.lastPaymentDate
              )}</p>
              <p><strong>Last Payment Amount:</strong> ${formatCurrency(
                account?.result?.lastPaymentAmount
              )}</p>
              <p><strong>Risk Score:</strong> Level ${
                account?.result?.riskScore
              }</p>
            </div>

            <div class="section">
              <h2>Contact Information</h2>
              <p><strong>Customer Service:</strong> (555) 123-4567</p>
              <p><strong>Office Hours:</strong> Monday - Friday, 8:00 AM - 5:00 PM</p>
              <p><strong>Address:</strong> 123 Utility Street, Water City, WC 12345</p>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleSendReminder = () => {
    setShowReminderDialog(true);
  };

  const handleSendNotice = () => {
    setShowNoticeDialog(true);
  };

  const handleSendStatement = () => {
    setShowStatementDialog(true);
  };

  const handleViewAgreementDocument = () => {
    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Payment Agreement - ${account?.result?.consumerNo}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
              .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
              .section { margin-bottom: 25px; }
              .table { width: 100%; border-collapse: collapse; margin: 15px 0; }
              .table th, .table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              .table th { background-color: #f5f5f5; }
              @media print { body { margin: 20px; } }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>PAYMENT AGREEMENT</h1>
              <p>Agreement ID: ${agreementDetail?.result?.agreementNo}</p>
              <p>Created Date: ${formatDate(
                agreementDetail?.result?.createdDate
              )}</p>
            </div>
            
            <div class="section">
              <h2>Account Information</h2>
              <p><strong>Account Number:</strong> ${
                account?.result?.consumerNo
              }</p>
              <p><strong>Customer Name:</strong> ${
                account?.result?.firstName + " " + account?.result?.lastName
              }</p>
              <p><strong>Service Address:</strong> ${account?.result?.addressData?.area}, ${account?.result?.addressData?.subArea}, ${account?.result?.addressData?.premise}</p>
            </div>

            <div class="section">
              <h2>Agreement Terms</h2>
              <p><strong>Total Amount:</strong> ${formatCurrency(
                agreementDetail?.result?.totalAgreementAmount
              )}</p>
              <p><strong>Down Payment:</strong> ${formatCurrency(
                agreementDetail?.result?.downPayment
              )}</p>
              <p><strong>Monthly Payment:</strong> ${formatCurrency(
                agreementDetail?.result?.monthlyPayment
              )}</p>
              <p><strong>Number of Installments:</strong> ${
                agreementDetail?.result?.noOfInstallments
              }</p>
              <p><strong>Status:</strong> ${
                agreementDetail?.result?.statusDisplay
              }</p>
              <p><strong>Created By:</strong> ${
                agreementDetail?.result?.createdUserRemoteName || "N/A"
              }</p>
            </div>
            
            <div class="section">
              <h2>Installment Schedule</h2>
              <table class="table">
                <thead>
                  <tr>
                    <th>Installment #</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Paid Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${agreementDetail?.result?.installments
                    ?.map(
                      (installment) => `
                    <tr>
                      <td>${installment.installmentNo}</td>
                      <td>${formatCurrency(installment.installmentAmount)}</td>
                      <td>${installment.statusDisplay}</td>
                      <td>${
                        installment.paidAmount
                          ? formatCurrency(installment.paidAmount)
                          : "-"
                      }</td>
                    </tr>
                  `
                    )
                    .join("")}
                </tbody>
              </table>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleCancelAgreement = () => {
    deletePaymentAgreement.mutate(agreementDetail?.result?.id);
    const cancelHistoryItem: HistoryItem = {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      type: "note",
      description: "Payment agreement cancelled",
      staff: "Current User",
    };
    setHistory((prev) => [cancelHistoryItem, ...prev]);
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      const note: Note = {
        id: Date.now().toString(),
        date: new Date().toISOString(),
        content: newNote.trim(),
        staff: "Current User",
      };
      setNotes((prev) => [note, ...prev]);
      setNewNote("");
    }
  };

  const handleViewHistoryItem = (item: HistoryItem) => {
    setViewingHistoryItem(item);
  };

  const handlePrintHistoryItem = (item: HistoryItem) => {
    if (item.type === "agreement" && item.details) {
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Payment Agreement - ${
                account?.result?.accountNumber
              }</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
                .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
                .section { margin-bottom: 25px; }
                @media print { body { margin: 20px; } }
              </style>
            </head>
            <body>
              <div class="header">
                <h1>PAYMENT AGREEMENT</h1>
                <p>Agreement Date: ${formatDate(item.date)}</p>
              </div>
              <div class="section">
                <h2>Account Information</h2>
                <p><strong>Account Number:</strong> ${
                  account?.result?.accountNumber
                }</p>
                <p><strong>Customer Name:</strong> ${
                  account?.result?.customerName
                }</p>
                <p><strong>Service Address:</strong> ${
                  account?.result?.serviceAddress
                }</p>
              </div>
              <div class="section">
                <h2>Agreement Terms</h2>
                <p><strong>Total Amount:</strong> ${formatCurrency(
                  item.details.totalAmount
                )}</p>
                <p><strong>Down Payment:</strong> ${formatCurrency(
                  item.details.downPayment
                )}</p>
                <p><strong>Monthly Payment:</strong> ${formatCurrency(
                  item.details.monthlyPayment
                )}</p>
                <p><strong>Number of Installments:</strong> ${
                  item.details.installments
                }</p>
              </div>
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    } else if (item.type === "notice" && item.details) {
      const printWindow = window.open("", "_blank");
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
            <head>
              <title>Disconnection Notice - ${
                account?.result?.accountNumber
              }</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 40px; line-height: 1.6; }
                .header { text-align: center; border-bottom: 2px solid #dc2626; padding-bottom: 20px; margin-bottom: 30px; }
                .notice-box { border: 3px solid #dc2626; padding: 20px; margin: 20px 0; background-color: #fef2f2; }
                .warning { color: #dc2626; font-weight: bold; }
                @media print { body { margin: 20px; } }
              </style>
            </head>
            <body>
              <div class="header">
                <h1 class="warning">FINAL NOTICE OF DISCONNECTION</h1>
                <p>Notice Date: ${formatDate(item.date)}</p>
              </div>
              <div class="notice-box">
                <h2 class="warning">SERVICE WILL BE DISCONNECTED</h2>
                <p><strong>Disconnection Date:</strong> ${formatDate(
                  item.details.disconnectionDate
                )}</p>
                <p><strong>Outstanding Amount:</strong> ${formatCurrency(
                  item.details.outstandingAmount
                )}</p>
              </div>
              <div class="section">
                <h2>Account Information</h2>
                <p><strong>Account Number:</strong> ${
                  account?.result?.accountNumber
                }</p>
                <p><strong>Customer Name:</strong> ${
                  account?.result?.customerName
                }</p>
                <p><strong>Service Address:</strong> ${
                  account?.result?.serviceAddress
                }</p>
              </div>
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  const handleAgreementCreated = (agreementDetails: any) => {
    const newHistoryItem: HistoryItem = {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      type: "agreement",
      description: `Payment agreement created - ${agreementDetails.installments} installments`,
      staff: "Current User",
      details: agreementDetails,
    };
    setHistory((prev) => [newHistoryItem, ...prev]);
    setShowAgreementDialog(false);
  };

  const handleNoticeSent = (noticeDetails: any) => {
    const newHistoryItem: HistoryItem = {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      type: "notice",
      description: `Disconnection notice sent - Due: ${formatDate(
        noticeDetails.disconnectionDate
      )}`,
      staff: "Current User",
      details: noticeDetails,
    };
    setHistory((prev) => [newHistoryItem, ...prev]);
    setShowNoticeDialog(false);
  };

  const handleReminderSent = (reminderDetails: any) => {
    const newHistoryItem: HistoryItem = {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      type: "communication",
      description: `Payment reminder sent - ${reminderDetails.reminderType}`,
      staff: "Current User",
      details: reminderDetails,
    };
    setHistory((prev) => [newHistoryItem, ...prev]);
    setShowReminderDialog(false);
  };

  const handleStatementSent = (statementDetails: any) => {
    const newHistoryItem: HistoryItem = {
      id: Date.now().toString(),
      date: new Date().toISOString(),
      type: "communication",
      description: `Account statement sent`,
      staff: "Current User",
      details: statementDetails,
    };
    setHistory((prev) => [newHistoryItem, ...prev]);
    setShowStatementDialog(false);
  };

  // Tab configuration
  const tabComponents = {
    actions: {
      label: "Actions",
      shortLabel: "Actions",
      icon: <CreditCard size={16} />,
      component: (
        <ActionsTab
          onProcessPayment={handleProcessPayment}
          onCreateAgreement={handleCreateAgreement}
          onSendReminder={handleSendReminder}
          onSendNotice={handleSendNotice}
        />
      ),
    },
    ...(hasActiveAgreement && {
      installments: {
        label: "Installments",
        shortLabel: "Installments",
        icon: <Calendar size={16} />,
        component: (
          <InstallmentsTab
            account={account?.result}
            agreementDetails={agreementDetails}
            installmentSchedule={installmentSchedule}
            onViewAgreementDocument={handleViewAgreementDocument}
            onCancelAgreement={handleCancelAgreement}
            formatCurrency={formatCurrency}
            formatDate={formatDate}
          />
        ),
      },
    }),
  };

  const urlMapping = {
    actions: "actions",
    installments: "installments",
  };

  // Handle error state
  if (error || !account) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">

        </div>
      </div>
    );
  }

  return (
    <>
      <div className="">
        <div className="flex justify-between">
          <span className="flex gap-2 items-center">
            <h1 className="page-subheading capitalize">
            {account?.result?.firstName} {account?.result?.lastName} - {account?.result?.consumerNo}
          </h1>
          <p className="card-subheading-text"></p>
          </span>
          <Button
            variant="ghost"
            onClick={() => navigate("/cx/outstanding")}
            className="mb-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Outstanding Accounts
          </Button>
        </div>

        <div className="space-y-6">
          {/* Balance Summary Card */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Balance Summary</CardTitle>
              <CardDescription>
                Overview of the current outstanding balance
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="card-value-text mb-4">
                {formatCurrency(
                  account?.result?.billData?.outstandingBalance ||
                    totalOutstanding
                )}
              </div>

              <div className="grid grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="card-subheading-text">
                    Days Outstanding
                  </p>
                  <p className="text-sm">
                    {account?.result?.billData?.daysSinceLastBill ||
                      account?.result?.daysOutstanding}{" "}
                    days
                  </p>
                </div>
                <div>
                  <p className="card-subheading-text">Last Payment</p>
                  <p className="text-sm">
                    {account?.result?.billData?.lastPaymentDate ||
                      account?.result?.lastPaymentDate}
                  </p>
                </div>
                <div>
                  <p className="card-subheading-text flex items-center gap-2">Risk Score <CustomTooltip message="Low: <30 days | Medium: 31–60 days | High: 61–90 days | Critical: 90+ days" /> </p>
                  <Badge variant="outline">
                    Level {account?.result?.riskFactor}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tabs for Account Details */}
          <Tabs
            defaultValue="actions"
            urlMapping={urlMapping}
            tabComponents={tabComponents}
            tabsListClassName={`grid w-full grid-cols-${
              Object.keys(tabComponents).length
            } mb-6`}
            idPrefix="account-detail-tab"
            level={0}
          />
        </div>
      </div>

      {/* History Item View Dialog */}
      {viewingHistoryItem && (
        <Dialog
          open={!!viewingHistoryItem}
          onOpenChange={() => setViewingHistoryItem(null)}
        >
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {viewingHistoryItem.type === "agreement"
                  ? "Payment Agreement"
                  : "Disconnection Notice"}{" "}
                Details
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Date:</span>
                  <p className="text-sm">
                    {viewingHistoryItem.date}
                  </p>
                </div>
                <div>
                  <span className="text-muted-foreground">Created By:</span>
                  <p className="text-sm">{viewingHistoryItem.staff}</p>
                </div>
              </div>

              {viewingHistoryItem.type === "agreement" &&
                viewingHistoryItem.details && (
                  <Card>
                    <CardContent className="p-4">
                      <h4 className="font-medium mb-2">Agreement Terms</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">
                            Total Amount:
                          </span>
                          <p className="font-medium">
                            {formatCurrency(
                              viewingHistoryItem.details.totalAmount
                            )}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            Down Payment:
                          </span>
                          <p className="font-medium">
                            {formatCurrency(
                              viewingHistoryItem.details.downPayment
                            )}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            Monthly Payment:
                          </span>
                          <p className="font-medium">
                            {formatCurrency(
                              viewingHistoryItem.details.monthlyPayment
                            )}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            Installments:
                          </span>
                          <p className="font-medium">
                            {viewingHistoryItem.details.installments} months
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

              {viewingHistoryItem.type === "notice" &&
                viewingHistoryItem.details && (
                  <Card>
                    <CardContent className="p-4">
                      <h4 className="font-medium mb-2">Notice Details</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">
                            Outstanding Amount:
                          </span>
                          <p className="font-medium">
                            {formatCurrency(
                              viewingHistoryItem.details.outstandingAmount
                            )}
                          </p>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            Disconnection Date:
                          </span>
                          <p className="font-medium">
                            {formatDate(
                              viewingHistoryItem.details.disconnectionDate
                            )}
                          </p>
                        </div>
                      </div>
                      {viewingHistoryItem.details.additionalNotes && (
                        <div className="mt-2">
                          <span className="text-muted-foreground">
                            Additional Notes:
                          </span>
                          <p className="text-sm mt-1">
                            {viewingHistoryItem.details.additionalNotes}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

              <div className="flex gap-2">
                <Button
                  onClick={() => handlePrintHistoryItem(viewingHistoryItem)}
                  className="flex-1"
                >
                  <Printer className="mr-2 h-4 w-4" />
                  Print
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setViewingHistoryItem(null)}
                  className="flex-1"
                >
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Dialogs */}
      <PaymentAgreementDialog
        isOpen={showAgreementDialog}
        onClose={() => setShowAgreementDialog(false)}
        account={account?.result}
        monthlyBills={monthlyBills}
        onAgreementCreated={handleAgreementCreated}
      />

      <DisconnectionNoticeDialog
        isOpen={showNoticeDialog}
        onClose={() => setShowNoticeDialog(false)}
        account={account?.result}
        outstandingAmount={totalOutstanding}
      />

      <SendReminderDialog
        isOpen={showReminderDialog}
        onClose={() => setShowReminderDialog(false)}
        account={account?.result}
        outstandingAmount={totalOutstanding}
        onReminderSent={handleReminderSent}
      />

      <SendStatementDialog
        isOpen={showStatementDialog}
        onClose={() => setShowStatementDialog(false)}
        account={account?.result}
        monthlyBills={monthlyBills}
        onStatementSent={handleStatementSent}
      />
    </>
  );
}
